import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Audience } from '@epasu/core';
import { CAMPAIGN_STATUS_LABELS } from '@epasu/core/constants';
import { Megaphone, Plus, Trash2 } from 'lucide-react';
import { crm, type CampaignRow } from '@/lib/crm-client';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { PageHeader } from '@/components/PageHeader';
import { CampaignDialog } from '@/components/CampaignDialog';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { zeitplanInWorten } from '@/lib/campaign-schedule';

const STATUS_VARIANTE: Record<CampaignRow['status'], NonNullable<BadgeProps['variant']>> = {
  entwurf: 'neutral',
  aktiv: 'success',
  pausiert: 'warning',
  beendet: 'neutral',
};

/**
 * Übersicht aller Kampagnen.
 *
 * Fortschritt und Antwortquote kommen fertig aus `campaigns.list`
 * (`CampaignWithProgress`) — keine eigene Berechnung hier, das wäre ein
 * zweiter Ort für dieselbe Zahl. `campaigns.metrics` für die volle
 * Auswertung folgt erst in Stufe C.
 */
export function CampaignsPage() {
  const navigate = useNavigate();
  const [kampagnen, setKampagnen] = useState<CampaignRow[] | null>(null);
  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [neu, setNeu] = useState(false);
  const [beendeteZeigen, setBeendeteZeigen] = useState(false);
  const [loeschen, setLoeschen] = useState<CampaignRow | null>(null);

  const laden = useCallback(() => {
    setFehler(null);
    crm.campaigns.list().then(setKampagnen, (err: unknown) =>
      setFehler(err instanceof Error ? err.message : 'Laden fehlgeschlagen'),
    );
  }, []);
  useEffect(laden, [laden]);

  useEffect(() => {
    void crm.audiences.list().then(setAudiences);
  }, []);

  // Beendete Kampagnen bleiben erhalten — sie belegen, an wen im Rahmen
  // welcher Kampagne Post ging — stehen aber nicht mehr im Weg.
  const beendete = (kampagnen ?? []).filter((k) => k.status === 'beendet').length;
  const sichtbar = useMemo(
    () => (kampagnen ?? []).filter((k) => beendeteZeigen || k.status !== 'beendet'),
    [kampagnen, beendeteZeigen],
  );

  return (
    <div>
      <PageHeader>
        <div className="flex flex-wrap items-center gap-2">
          <label className="ml-auto flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={beendeteZeigen}
              onChange={(e) => setBeendeteZeigen(e.target.checked)}
            />
            Beendete einblenden{beendete > 0 && ` (${beendete})`}
          </label>
          <Button onClick={() => setNeu(true)}>
            <Plus className="mr-1.5 h-4 w-4" />
            Neue Kampagne
          </Button>
        </div>
      </PageHeader>

      {fehler && (
        <Card className="mb-4 border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          {fehler}
        </Card>
      )}

      <Card className="overflow-hidden">
        {kampagnen === null ? (
          <p className="p-6 text-sm text-muted-foreground">Lade …</p>
        ) : sichtbar.length === 0 ? (
          <p className="p-8 text-center text-sm text-muted-foreground">
            <Megaphone className="mx-auto mb-3 h-6 w-6" />
            {kampagnen.length === 0
              ? 'Noch keine Kampagne. „Neue Kampagne“ legt die erste an.'
              : 'Alle Kampagnen sind beendet — „Beendete einblenden“ zeigt sie.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Zielgruppe</th>
                  <th className="px-4 py-3 font-medium">Fortschritt</th>
                  <th className="px-4 py-3 font-medium">Antwortquote</th>
                  <th className="px-4 py-3 font-medium">Öffnungsquote</th>
                  <th className="px-4 py-3 font-medium">Tageslimit</th>
                  <th className="px-4 py-3 font-medium">Zeitplan</th>
                  <th className="px-4 py-3 font-medium" />
                </tr>
              </thead>
              <tbody>
                {sichtbar.map((k) => {
                  const antwortquote = k.sentCount > 0 ? Math.round((k.repliedCount / k.sentCount) * 100) : null;
                  const oeffnungsquote =
                    k.trackOpens && k.sentCount > 0
                      ? Math.round((k.openedCount / k.sentCount) * 100)
                      : null;
                  return (
                    <tr
                      key={k.id}
                      className="cursor-pointer border-b last:border-0 hover:bg-muted/40"
                      onClick={() => navigate(`/leads/kampagnen/${k.id}`)}
                    >
                      <td className="px-4 py-3 font-medium">{k.name}</td>
                      <td className="px-4 py-3">
                        <Badge variant={STATUS_VARIANTE[k.status]}>
                          {CAMPAIGN_STATUS_LABELS[k.status]}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{k.audienceName || '—'}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {k.sentCount} / {k.memberCount}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {antwortquote === null ? '—' : `${antwortquote} %`}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {oeffnungsquote === null ? '—' : `${oeffnungsquote} %`}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{k.dailyLimit}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {zeitplanInWorten(k) ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`${k.name} löschen`}
                          title="Löschen"
                          // Ohne das Anhalten öffnete der Klick zugleich die
                          // Kampagne, auf deren Zeile er sitzt.
                          onClick={(e) => {
                            e.stopPropagation();
                            setLoeschen(k);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {loeschen && (
        <DeleteCampaignDialog
          kampagne={loeschen}
          onClose={() => setLoeschen(null)}
          onDeleted={() => {
            setLoeschen(null);
            laden();
          }}
        />
      )}

      {neu && (
        <CampaignDialog
          audiences={audiences}
          onClose={() => setNeu(false)}
          onSaved={(campaignId) => {
            setNeu(false);
            navigate(`/leads/kampagnen/${campaignId}`);
          }}
        />
      )}
    </div>
  );
}

/**
 * Löschen fragt nach — und sagt, wenn es nicht geht.
 *
 * Sobald aus der Kampagne Post hinausging, verweigert `campaigns.delete`:
 * die versendeten Mails führen die Kampagne, und ohne sie stünde im Protokoll
 * Post ohne Anlass. Solche Kampagnen werden beendet, nicht gelöscht — die
 * Übersicht blendet beendete ohnehin aus.
 */
function DeleteCampaignDialog({
  kampagne,
  onClose,
  onDeleted,
}: {
  kampagne: CampaignRow;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [blockiert, setBlockiert] = useState(false);

  return (
    <ConfirmDialog
      title={`${kampagne.name} löschen?`}
      confirmLabel="Löschen"
      destructive
      onCancel={onClose}
      onConfirm={async () => {
        const ergebnis = await crm.campaigns.delete(kampagne.id);
        if (ergebnis.deleted) {
          onDeleted();
        } else {
          setBlockiert(true);
        }
      }}
    >
      {blockiert ? (
        <p className="rounded-md bg-secondary p-3 text-sm text-muted-foreground">
          Aus dieser Kampagne ist schon Post hinausgegangen — löschen ginge nur
          auf Kosten des Nachweises, wer wann angeschrieben wurde. Setz sie
          stattdessen auf „Beendet“; die Übersicht blendet beendete Kampagnen aus.
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">
          Mitglieder und Schritte dieser Kampagne verschwinden mit ihr. Die
          Kontakte selbst bleiben erhalten.
        </p>
      )}
    </ConfirmDialog>
  );
}
